/**
 * A2A Calendar Service
 * Wraps calendar operations using A2A client instead of direct Supabase calls
 */

import { A2AClient } from '../client.js';
import { getAuthToken } from '../authHelper.js';

// Initialize A2A client with default backend URL and authentication
const a2aClient = new A2AClient(
  process.env.REACT_APP_A2A_BACKEND_URL || process.env.REACT_APP_API_URL || 'https://a2amyclassai-server.appsokay.net',
  'myclass-client-calendar',
  {
    secretKey: process.env.REACT_APP_A2A_SECRET_KEY,
    enableLogging: process.env.NODE_ENV === 'development',
    getAuthToken: getAuthToken
  }
);

/**
 * A2A Calendar Service
 */
export const a2aCalendarService = {
  /**
   * Get events
   */
  async getEvents(params) {
    const { tenantId, startDate, endDate, eventType } = params;
    
    const result = await a2aClient.executeTask('list_events', {
      tenant_id: tenantId,
      start_date: startDate,
      end_date: endDate,
      event_type: eventType
    });

    return {
      success: result.success,
      data: result.data || []
    };
  },

  /**
   * Get event by ID
   */
  async getEventById(eventId, tenantId) {
    const result = await a2aClient.executeTask('get_event', {
      tenant_id: tenantId,
      event_id: eventId
    });

    return {
      success: result.success,
      data: result.event
    };
  },

  /**
   * Create event
   */
  async createEvent(eventData) {
    const result = await a2aClient.executeTask('create_event', {
      tenant_id: eventData.tenant_id,
      event_data: eventData
    });

    return {
      success: result.success,
      data: result.event
    };
  },

  /**
   * Update event
   */
  async updateEvent(eventId, tenantId, eventData) {
    const result = await a2aClient.executeTask('update_event', {
      tenant_id: tenantId,
      event_id: eventId,
      event_data: eventData
    });

    return {
      success: result.success,
      data: result.event
    };
  },

  /**
   * Delete event
   */
  async deleteEvent(eventId, tenantId) {
    const result = await a2aClient.executeTask('delete_event', {
      tenant_id: tenantId,
      event_id: eventId
    });

    return {
      success: result.success
    };
  },

  /**
   * Get academic years
   */
  async getAcademicYears(tenantId) {
    const result = await a2aClient.executeTask('list_academic_years', {
      tenant_id: tenantId
    });

    return {
      success: result.success,
      data: result.academicYears || []
    };
  },

  /**
   * Get current academic year
   */
  async getCurrentAcademicYear(tenantId) {
    const result = await a2aClient.executeTask('get_current_academic_year', {
      tenant_id: tenantId
    });

    return {
      success: result.success,
      data: result.academicYear
    };
  },

  /**
   * Get holidays
   */
  async getHolidays(tenantId, params = {}) {
    const result = await a2aClient.executeTask('list_holidays', {
      tenant_id: tenantId,
      academic_year: params.academicYear
    });

    return {
      success: result.success,
      data: result.holidays || []
    };
  },

  /**
   * Get cycle day
   */
  async getCycleDay(date, tenantId) {
    const result = await a2aClient.executeTask('get_cycle_day', {
      tenant_id: tenantId,
      date
    });

    return {
      success: result.success,
      data: result.cycleDay
    };
  }
};

export default a2aCalendarService;

